import React, { useState, useEffect, useRef } from 'react';
import { getCached, setCached } from '../lib/listCache';
import usePullToRefresh from '../lib/usePullToRefresh';
import PullIndicator from './PullIndicator';
import API_BASE from '../config';
import { fetchCreditBalance } from '../lib/creditBalance';
import { openHubBilling } from '../lib/hubBilling';
import { formatInTimeZone, useDisplayTimeZone } from '../lib/displayTimezone';
import { dialogueTurns, dialogueToPlainText } from '../lib/recentsDialogue';
import { useLineContacts, isNumberSaved, savedContactName } from '../lib/lineContacts';
import AddToExistingContact from './AddToExistingContact';

function formatLineLabel(number) {
  const digits = String(number || '').replace(/\D/g, '');
  if (digits.length === 11 && digits.startsWith('1')) {
    return `(${digits.slice(1, 4)}) ${digits.slice(4, 7)}-${digits.slice(7)}`;
  }
  if (digits.length === 10) {
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
  }
  return number || '';
}

// Presentation-only helper for the ui/ios-05 restyle: an inbound call that was
// never answered is a missed call, which iOS recents shows with a red name.
// This only feeds the .log-missed modifier class — no behaviour depends on it.
const MISSED_STATUSES = ['no-answer', 'busy', 'failed', 'canceled', 'cancelled'];
function isMissedCall(call) {
  if (!call || call.direction !== 'inbound') return false;
  return MISSED_STATUSES.indexOf(String(call.status || '').toLowerCase()) !== -1;
}

export default function CallLog({ authToken, handleCall, setTab, selectedNumber, entitlements, setPrefillContactNumber, setComposeTo }) {
  const displayTz = useDisplayTimeZone();
  const [savedContacts, reloadSavedContacts, savedContactsLoaded] = useLineContacts(selectedNumber, authToken);
  const [logs, setLogs] = useState(() => getCached('calls', selectedNumber) || []);
  const [logsLoaded, setLogsLoaded] = useState(() => getCached('calls', selectedNumber) != null);
  const fetchCallsRef = useRef(null);
  const ptr = usePullToRefresh(() => (fetchCallsRef.current ? fetchCallsRef.current() : null));
  const [summaries, setSummaries] = useState({});
  const [expanded, setExpanded] = useState(null);
  const [hintFor, setHintFor] = useState(null); // row showing "upgrade to view" hint
  const [copiedSid, setCopiedSid] = useState(null); // call whose Copied feedback is showing
  const [translatedBySid, setTranslatedBySid] = useState({}); // sid -> translated turns
  const [transcriptViewBySid, setTranscriptViewBySid] = useState({}); // sid -> 'original'|'translated'
  const [translatingSid, setTranslatingSid] = useState(null);
  const [translateErrorBySid, setTranslateErrorBySid] = useState({});
  const [hasTranscripts, setHasTranscripts] = useState(
    entitlements && typeof entitlements.transcripts === 'boolean'
      ? !!entitlements.transcripts
      : null,
  );

  useEffect(() => {
    if (!authToken) {
      if (entitlements && typeof entitlements.transcripts === 'boolean') {
        setHasTranscripts(!!entitlements.transcripts);
      }
      return undefined;
    }
    let cancelled = false;
    (async () => {
      try {
        const data = await fetchCreditBalance(authToken);
        if (cancelled) return;
        const ents = data.entitlements || {};
        if (typeof ents.transcripts === 'boolean') {
          setHasTranscripts(!!ents.transcripts);
          return;
        }
        setHasTranscripts(!!(entitlements && entitlements.transcripts));
      } catch (_) {
        if (!cancelled) {
          setHasTranscripts(!!(entitlements && entitlements.transcripts));
        }
      }
    })();
    return () => { cancelled = true; };
  }, [authToken, entitlements]);

  useEffect(() => {
    const cachedForLine = getCached('calls', selectedNumber);
    setLogs(cachedForLine || []);
    setLogsLoaded(cachedForLine != null);
    const fetchCalls = async () => {
      const headers = {
        Authorization: `Bearer ${authToken}`,
        'ngrok-skip-browser-warning': 'true',
      };
      try {
        const qs = selectedNumber
          ? `?line=${encodeURIComponent(selectedNumber)}`
          : '';
        const res = await fetch(`${API_BASE}/calls${qs}`, { headers });
        if (res.ok) {
          const data = await res.json().catch(() => null);
          // Server already returns newest first — do not reverse.
          if (Array.isArray(data)) {
            setLogs(data);
            setCached('calls', selectedNumber, data);
          }
        }
        setLogsLoaded(true);
      } catch (err) {
        console.error('Failed to fetch call log:', err);
      }
    };

    fetchCallsRef.current = fetchCalls;
    fetchCalls();
    const interval = setInterval(fetchCalls, 5000);
    return () => { clearInterval(interval); fetchCallsRef.current = null; };
  }, [authToken, selectedNumber]);

  const fetchSummary = async (callSid) => {
    if (!hasTranscripts) return;
    const headers = {
      Authorization: `Bearer ${authToken}`,
      'ngrok-skip-browser-warning': 'true',
    };
    const done = (payload) => {
      setSummaries((prev) => ({ ...prev, [callSid]: payload }));
    };
    try {
      const res = await fetch(`${API_BASE}/api/calls/${callSid}/summary`, { headers });
      const data = await res.json().catch(() => null);
      if (res.ok && data && typeof data === 'object') {
        const text = (data.summary || '').trim();
        let status = data.summary_status || (text ? 'completed' : 'not_found');
        if (status === 'pending' && !text) status = 'not_found';
        done({
          summary: data.summary || '',
          action_items: data.action_items || [],
          transcript: Array.isArray(data.transcript) ? data.transcript : [],
          summary_status: status,
        });
        return;
      }
      done({
        summary: '',
        action_items: [],
        summary_status: res.status === 403 ? 'forbidden' : 'not_found',
      });
    } catch (err) {
      done({ summary: '', action_items: [], summary_status: 'unavailable' });
    }
  };

  const toggleExpand = (sid) => {
    if (!hasTranscripts) {
      // Don't fetch/expand a summary — just show why nothing happens, instead
      // of the previous silent no-op.
      setHintFor((prev) => (prev === sid ? null : sid));
      return;
    }
    if (expanded === sid) {
      setExpanded(null);
    } else {
      setExpanded(sid);
      fetchSummary(sid);
    }
  };

  const copyTranscript = async (callSid, summary, viewTranscript) => {
    const text = dialogueToPlainText({
      transcript: viewTranscript != null ? viewTranscript : (summary && summary.transcript),
      summary: viewTranscript != null ? '' : (summary && summary.summary),
    });
    if (!text) return;
    try {
      await navigator.clipboard.writeText(text);
      setCopiedSid(callSid);
      setTimeout(() => {
        setCopiedSid((prev) => (prev === callSid ? null : prev));
      }, 1800);
    } catch (err) {
      console.error('Failed to copy transcript:', err);
    }
  };

  const translateTranscript = async (callSid) => {
    if (!callSid || translatingSid === callSid) return;
    setTranslateErrorBySid((prev) => {
      const next = { ...prev };
      delete next[callSid];
      return next;
    });
    setTranslatingSid(callSid);
    const headers = {
      Authorization: `Bearer ${authToken}`,
      'ngrok-skip-browser-warning': 'true',
      'Content-Type': 'application/json',
    };
    try {
      const res = await fetch(`${API_BASE}/api/calls/${callSid}/translate-transcript`, {
        method: 'POST',
        headers,
        body: '{}',
      });
      const data = await res.json().catch(() => null);
      if (!res.ok || !data || !Array.isArray(data.transcript)) {
        const msg = (data && (data.error || data.message)) || `Translate failed (${res.status})`;
        setTranslateErrorBySid((prev) => ({ ...prev, [callSid]: msg }));
        return;
      }
      setTranslatedBySid((prev) => ({ ...prev, [callSid]: data.transcript }));
      setTranscriptViewBySid((prev) => ({ ...prev, [callSid]: 'translated' }));
    } catch (err) {
      console.error('Failed to translate transcript:', err);
      setTranslateErrorBySid((prev) => ({ ...prev, [callSid]: 'Translate failed' }));
    } finally {
      setTranslatingSid((prev) => (prev === callSid ? null : prev));
    }
  };

  const toggleTranscriptView = (callSid) => {
    setTranscriptViewBySid((prev) => {
      const cur = prev[callSid] || 'original';
      return { ...prev, [callSid]: cur === 'translated' ? 'original' : 'translated' };
    });
  };

  const DirectionBadge = ({ direction }) => {
    const inbound = direction === 'inbound';
    return (
      <span
        className={`log-icon ${inbound ? 'log-icon-inbound' : 'log-icon-outbound'}`}
        aria-label={inbound ? 'Inbound call' : 'Outbound call'}
      >
        {inbound ? (
          <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="M19 5 L5 19" />
            <path d="M5 19 L12 19" />
            <path d="M5 19 L5 12" />
          </svg>
        ) : (
          <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="M5 19 L19 5" />
            <path d="M19 5 L12 5" />
            <path d="M19 5 L19 12" />
          </svg>
        )}
      </span>
    );
  };
  const fmtTime = (ts) => formatInTimeZone(ts, displayTz);
  const fmtDuration = (s) => {
    const n = parseInt(s, 10);
    if (!n || isNaN(n)) return '';
    if (n < 60) return `${n}s`;
    const m = Math.floor(n / 60);
    const r = n % 60;
    return r ? `${m}m ${r}s` : `${m}m`;
  };

  const handleTap = (number) => {
    if (!number || !handleCall) return;
    if (setTab) setTab('phone');
    handleCall(number);
  };

  const lineLabel = selectedNumber ? formatLineLabel(selectedNumber) : '';

  return (
    <div className="call-log" ref={ptr.ref}>
      <PullIndicator pull={ptr.pull} refreshing={ptr.refreshing} threshold={ptr.threshold} />
      {lineLabel ? (
        <h3 className="call-log-line">Line {lineLabel}</h3>
      ) : (
        <h3>Recents</h3>
      )}
      {logs.length === 0 && !logsLoaded && (
        <p className="empty-hint">Loading…</p>
      )}
      {logs.length === 0 && logsLoaded && (
        <p className="empty-hint">
          {selectedNumber
            ? 'No calls on this line yet'
            : 'No calls yet'}
        </p>
      )}
      <ul>
        {logs.map((c, i) => {
          const number = c.direction === 'inbound' ? c.from : c.to;
          const lineNumber = c.direction === 'inbound' ? c.to : c.from;
          const isExpanded = expanded === c.sid;
          const summary = summaries[c.sid];
          const turns = isExpanded && summary ? dialogueTurns(summary) : [];
          const translatedTurns = translatedBySid[c.sid];
          const viewMode = transcriptViewBySid[c.sid] || 'original';
          const showingTranslated = viewMode === 'translated' && Array.isArray(translatedTurns) && translatedTurns.length > 0;
          const displayTurns = showingTranslated
            ? dialogueTurns({ transcript: translatedTurns })
            : turns;
          const viewTranscript = showingTranslated ? translatedTurns : (summary && summary.transcript);
          const translateErr = translateErrorBySid[c.sid];
          const isTranslating = translatingSid === c.sid;
          return (
            <li
              key={c.sid || i}
              className={`log-entry ${isExpanded ? 'log-entry-expanded' : ''}${isMissedCall(c) ? ' log-missed' : ''}`}
              onClick={() => toggleExpand(c.sid)}
              style={{ cursor: 'pointer' }}
            >
              <div className="log-main">
                <DirectionBadge direction={c.direction} />
                <div className="log-details">
                  <span className="log-number">{savedContactName(savedContacts, number) || number}</span>
                  <span className="log-meta">
                    {c.status} &middot; {fmtTime(c.timestamp)}
                    {c.duration ? ` · ${fmtDuration(c.duration)}` : ''}
                  </span>
                  {!selectedNumber && lineNumber ? (
                    <span className="log-line-meta">via {formatLineLabel(lineNumber)}</span>
                  ) : null}
                </div>
                {handleCall && (
                  <span
                    className="log-call-btn"
                    onClick={(e) => { e.stopPropagation(); handleTap(number); }}
                  >
                    📞
                  </span>
                )}
                {typeof setComposeTo === 'function' && (
                  <span
                    className="log-call-btn"
                    title="Message"
                    aria-label={`Message ${number}`}
                    onClick={(e) => { e.stopPropagation(); setComposeTo(number); if (setTab) setTab('messages'); }}
                  >
                    ✉
                  </span>
                )}
                {typeof setPrefillContactNumber === 'function' && !savedContactsLoaded && (
                  // Hold the slot until we know whether this number is saved,
                  // so the row doesn't reflow when the icon is decided.
                  <span className="log-call-btn" aria-hidden="true" style={{ visibility: 'hidden' }}>➕</span>
                )}
                {typeof setPrefillContactNumber === 'function' && savedContactsLoaded && (isNumberSaved(savedContacts, number) ? (
                  <span className="log-action-slot">
                    <AddToExistingContact
                      selectedNumber={selectedNumber}
                      authToken={authToken}
                      number={number}
                      contacts={savedContacts}
                      onAdded={reloadSavedContacts}
                    />
                  </span>
                ) : (
                  <span
                    className="log-call-btn"
                    title="Add to Contacts"
                    aria-label={`Add ${number} to Contacts`}
                    onClick={(e) => { e.stopPropagation(); setPrefillContactNumber(number); if (setTab) setTab('contacts'); }}
                  >
                    ➕
                  </span>
                ))}
              </div>
              {isExpanded && summary && (
                <div className="log-summary">
                  {turns.length > 0 && (
                    <div className="log-transcript">
                      <div className="log-transcript-header">
                        <span className="log-transcript-title">Transcript</span>
                        <div className="log-transcript-actions">
                          <button
                            type="button"
                            className={`log-copy-transcript-btn${copiedSid === c.sid ? ' copied' : ''}`}
                            onClick={(e) => { e.stopPropagation(); copyTranscript(c.sid, summary, viewTranscript); }}
                          >
                            {copiedSid === c.sid ? 'Copied' : 'Copy transcript'}
                          </button>
                          {Array.isArray(translatedTurns) && translatedTurns.length > 0 ? (
                            <button
                              type="button"
                              className="log-translate-transcript-btn"
                              onClick={(e) => { e.stopPropagation(); toggleTranscriptView(c.sid); }}
                            >
                              {showingTranslated ? 'Show original' : 'Show English'}
                            </button>
                          ) : (
                            <button
                              type="button"
                              className={`log-translate-transcript-btn${isTranslating ? ' translating' : ''}${translateErr ? ' errored' : ''}`}
                              disabled={isTranslating}
                              onClick={(e) => { e.stopPropagation(); translateTranscript(c.sid); }}
                            >
                              {isTranslating ? 'Translating…' : (translateErr ? 'Retry translate' : 'Translate to English')}
                            </button>
                          )}
                        </div>
                      </div>
                      {translateErr ? (
                        <p className="log-translate-error">{translateErr}</p>
                      ) : null}
                      {displayTurns.map((turn, idx) => (
                        <div key={idx} className="log-turn">
                          {turn.who ? (
                            <div className="log-turn-label">{turn.who}</div>
                          ) : null}
                          {turn.sentences.map((sentence, sidx) => (
                            <p key={sidx} className="log-turn-text">{sentence}</p>
                          ))}
                        </div>
                      ))}
                    </div>
                  )}
                  {summary.action_items && summary.action_items.length > 0 && (
                    <div className="log-summary-actions">
                      <strong>Action items:</strong>
                      <ul>
                        {summary.action_items.map((item, idx) => (
                          <li key={idx}>{item}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                  {summary.summary_status === 'pending' && (
                    <p className="log-summary-pending">Summary being generated...</p>
                  )}
                  {summary.summary_status === 'not_found' && (
                    <p className="log-summary-pending">No summary available for this call.</p>
                  )}
                  {summary.summary_status === 'unavailable' && (
                    <p className="log-summary-pending">Summary is temporarily unavailable.</p>
                  )}
                  {summary.summary_status === 'forbidden' && (
                    <p className="log-summary-pending">
                      Call summaries are available on Concierge and Business plans.
                    </p>
                  )}
                </div>
              )}
              {isExpanded && !summary && (
                <div className="log-summary">
                  <p className="log-summary-pending">Loading summary...</p>
                </div>
              )}
              {hintFor === c.sid && (
                <div className="log-summary">
                  <p className="log-summary-pending">
                    Call summaries are available on Concierge and Business plans.
                  </p>
                  <button
                    type="button"
                    className="log-summary-upgrade-btn"
                    onClick={(e) => { e.stopPropagation(); openHubBilling(); }}
                  >
                    Choose a plan
                  </button>
                </div>
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
}
